import { useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useVmsWorkspaceOptional } from '../contexts/VmsWorkspaceContext';
import { parseOnboardingReturnTo } from '../pages/vms/onboardingFlowUrls';

const PRE_ONBOARDING_IN_PROGRESS_VI_PATH =
  /^\/vms\/pre-onboarding\/in-progress\/(\d+)(?:\/|$)/;

function useSyncVendorInitiationToWorkspace(
  vendorInitiationId: number | null,
  returnTo: string | null,
): void {
  const workspace = useVmsWorkspaceOptional();
  const selectVendorInitiation = workspace?.selectVendorInitiation;
  const selectedVendorInitiationId = workspace?.selectedVendorInitiationId ?? null;
  const pickerVisible = workspace?.workspacePickerVisible ?? false;

  useEffect(() => {
    if (parseOnboardingReturnTo(returnTo)) return;
    if (!pickerVisible || !selectVendorInitiation || vendorInitiationId == null) return;
    if (selectedVendorInitiationId === vendorInitiationId) return;
    void selectVendorInitiation(vendorInitiationId);
  }, [
    returnTo,
    pickerVisible,
    selectVendorInitiation,
    selectedVendorInitiationId,
    vendorInitiationId,
  ]);
}

/** When `?vendor_initiation_id=` is present, sync the workspace service picker (e.g. from Pre Onboarding InProgress cards). */
export function useApplyVendorInitiationIdFromSearchParams(): void {
  const [searchParams] = useSearchParams();
  /** Primitive dep — avoid `searchParams` object identity (context value changes every summary refresh). */
  const vendorInitiationIdFromQuery = searchParams.get('vendor_initiation_id');
  const returnToFromQuery = searchParams.get('returnTo');

  const vendorInitiationId = useMemo(() => {
    if (!vendorInitiationIdFromQuery) return null;
    const id = Number.parseInt(vendorInitiationIdFromQuery, 10);
    return Number.isFinite(id) && id > 0 ? id : null;
  }, [vendorInitiationIdFromQuery]);

  useSyncVendorInitiationToWorkspace(vendorInitiationId, returnToFromQuery);
}

/**
 * When viewing a pre-onboarding journey route (`/vms/pre-onboarding/in-progress/:id` or nested step),
 * sync the header Service picker to that vendor initiation.
 */
export function useApplyVendorInitiationIdFromPreOnboardingRoute(): void {
  const { pathname } = useLocation();

  const vendorInitiationId = useMemo(() => {
    const match = pathname.match(PRE_ONBOARDING_IN_PROGRESS_VI_PATH);
    if (!match) return null;
    const id = Number.parseInt(match[1], 10);
    return Number.isFinite(id) && id > 0 ? id : null;
  }, [pathname]);

  useSyncVendorInitiationToWorkspace(vendorInitiationId, null);
}

/** Global VMS layout sync: query param + pre-onboarding journey routes. */
export function VmsWorkspaceSelectionSync(): null {
  useApplyVendorInitiationIdFromSearchParams();
  useApplyVendorInitiationIdFromPreOnboardingRoute();
  return null;
}
